import { ChangeDetectionStrategy, Component, computed, inject, OnInit, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DashboardDialogFramePart, DialogAction, DialogContext } from '@eforall/dashboard';
import { TabStripModule } from '@progress/kendo-angular-layout';
import { ExplorerService } from '../../../explorer';
import { PersonDialogDataService } from './person-dialog-data.service';
import { PersonEditorViewPart } from './person-editor-view/person-editor.part';
import { PersonExplorerViewPart } from './person-explorer-view/person-explorer.part';

export interface PersonDialogData {
	personId: number;
	toEdit: boolean;
	tab?: string;
}

@Component({
	selector: 'person-dialog',
	standalone: true,
	imports: [
		DashboardDialogFramePart,
		TabStripModule,
		PersonEditorViewPart,
		PersonExplorerViewPart
	],
	changeDetection: ChangeDetectionStrategy.Eager,
	templateUrl: './person.dialog.html'
})

export class PersonDialog implements OnInit {

	public dialogRef = inject(MatDialogRef<PersonDialog>);
	public dialogContext: DialogContext<PersonDialogData> = inject(MAT_DIALOG_DATA);

	public personDialogDataService = inject(PersonDialogDataService);
	#explorer = inject(ExplorerService);

	public readonly headerName = computed(() => {
		const person = this.personDialogDataService.personDetails();
		return person ? `Person: ${person.fullName}` : 'Person';
	})

	public toEdit = computed(() => this.personDialogDataService.toEdit());

	displayEditorView() {
		this.personDialogDataService.editModePersonTab.set('basicInfo');
		this.personDialogDataService.toEdit.update(value => !value);
	}

	public actions = computed<DialogAction[]>(() => {

		const closeBtnAction: DialogAction = {
			id: 'close',
			enabled: signal(true),
			visible: signal(true),
			cssClass: signal('btn-standard'),
			text: signal('Close'),
			linkType: 'no-action',
			willCloseDialog: true,
		};

		const details = this.personDialogDataService.personDetails();
		if (!details) return [closeBtnAction];

		if (this.toEdit()) {
			const personEditorTab = this.personDialogDataService.editModePersonTab();
			switch (personEditorTab) {
				case 'emails':
				case 'webLinks':
				case 'permissions':
					return [
						{
							id: 'view',
							enabled: signal(true),
							visible: signal(true),
							cssClass: signal('btn-standard'),
							text: signal('View'),
							linkType: 'actionClick-event',
							willCloseDialog: false,
						},
						closeBtnAction
					];
				default:
					return [];
			}
		} else if (this.personDialogDataService.viewModePersonTab() !== 'overview') {
			return [closeBtnAction];
		}

		return [
			{
				id: 'edit',
				enabled: signal(true),
				visible: signal(true),
				cssClass: signal('btn-standard'),
				text: signal('Edit'),
				linkType: 'actionClick-event',
				willCloseDialog: false,
			},
			closeBtnAction,
			{
				id: 'explorer',
				enabled: signal(true),
				visible: signal(true),
				cssClass: signal('btn-standard'),
				text: signal('Explore'),
				linkType: 'callback',
				callback: async () => {
					await this.dialogRef.close();
					await this.#explorer.show({ noun: 'Person', id: details.personId, name: details.fullName, hasRedFlag: false });
				},
				willCloseDialog: true,
			}
		]
	});

	ngOnInit() {
		this.personDialogDataService.toEdit.set(this.dialogContext.data?.toEdit ?? false);
		const allowedTabs = ["overview", "engagements", "notes"] as const;
		this.personDialogDataService.viewModePersonTab.set(this.dialogContext.data?.tab as typeof allowedTabs[number] || 'overview');
	}

}